import React, { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { AlertCircle, Clock, CheckCircle, Film } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { tomorrow } from 'react-syntax-highlighter/dist/esm/styles/prism';
import VideoHistory from './VideoHistory';
import ComparisonPanel from './ComparisonPanel';
import {
  VideoUploadSection,
  AnalysisStatusSection,
  KeyMeasurementsDisplay,
  VideoPlayerSection,
  VideoComparisonSetup,
  VideoComparisonViewer
} from './components/video';
import { API_URL } from './config/api';
import authService from './services/authService';
import { trackEvent } from './utils/analytics';

const VideoAnalysisUpload = ({ onAnalysisComplete, onAnalysisStart, onVideoLoaded }) => {
  // Safe number formatting helper
  const safeFixed = (value, decimals = 2) => {
    return (value != null && !isNaN(value)) ? Number(value).toFixed(decimals) : 'N/A';
  };

  const [uploadStatus, setUploadStatus] = useState('idle'); // idle, ready, uploading, processing, complete, error, too_long
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedElement, setSelectedElement] = useState('');
  const [analysisResult, setAnalysisResult] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [processingMessage, setProcessingMessage] = useState('');
  const [videoId, setVideoId] = useState(null);

  // Synchronized features state
  const [currentFrame, setCurrentFrame] = useState(0);
  const [frameCoaching, setFrameCoaching] = useState(null);
  const [coachingMap, setCoachingMap] = useState(null);

  // Overlay video state
  const [overlayVideoUrl, setOverlayVideoUrl] = useState(null);
  const [isGeneratingOverlay, setIsGeneratingOverlay] = useState(false);

  // Canvas overlay state (when burn-in video unavailable)
  const [useCanvasOverlay, setUseCanvasOverlay] = useState(false);
  const [totalFrameCount, setTotalFrameCount] = useState(0);
  const [videoFps, setVideoFps] = useState(30);

  // History viewing state
  const [viewingHistoryVideo, setViewingHistoryVideo] = useState(false);
  const [historyVideoData, setHistoryVideoData] = useState(null);

  // Comparison panel state
  const [showComparisonPanel, setShowComparisonPanel] = useState(false);

  // Phase correction state
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [isPhaseCorrected, setIsPhaseCorrected] = useState(false);

  // Video history cache state
  const [videoHistoryCache, setVideoHistoryCache] = useState(null);

  // Compare mode state
  const [mode, setMode] = useState('single');
  const [compareSlotA, setCompareSlotA] = useState(null);
  const [compareSlotB, setCompareSlotB] = useState(null);
  const [compareVideoA, setCompareVideoA] = useState(null);
  const [compareVideoB, setCompareVideoB] = useState(null);

  const reconstructAnalysisResultFromHistory = useCallback((analysis) => {
    let biomechData = analysis.biomechanical_results || {};
    if (typeof biomechData === 'string') {
      try {
        biomechData = JSON.parse(biomechData);
      } catch {
        biomechData = {};
      }
    }

    return {
      element_type: analysis.element_type || '',
      confidence_score: analysis.confidence_score || 0,
      processing_time: analysis.processing_time || 0,
      frame_count: analysis.frame_count || 0,
      total_video_frames: analysis.frame_count || 0,
      detected_poses: analysis.frame_count || 0,
      fps: analysis.fps || 30,
      pose_data: [],
      biomechanical_data: biomechData,
      coaching_feedback: analysis.coaching_feedback || '',
      created_at: analysis.created_at || null,
    };
  }, []);

  const fetchAnalysisResultWithRecovery = useCallback(async (videoId) => {
    const headers = { ...authService.getAuthHeader() };

    for (let attempt = 0; attempt < 4; attempt += 1) {
      const resultResponse = await fetch(`${API_URL}/api/v1/analysis-result/${videoId}`, { headers });
      const resultData = await resultResponse.json();

      if (resultData.success) {
        return { source: 'analysis-result', result: resultData.result };
      }

      if (resultResponse.status === 202 || resultResponse.status === 404) {
        if (attempt < 3) {
          await new Promise(resolve => setTimeout(resolve, 1000));
          continue;
        }
      } else {
        throw new Error(resultData.detail || 'Failed to retrieve analysis results');
      }
    }

    const historyResponse = await fetch(`${API_URL}/api/v1/video-history/${videoId}`, { headers });
    const historyData = await historyResponse.json();
    if (historyResponse.ok && historyData.success && historyData.analysis) {
      return {
        source: 'history',
        result: reconstructAnalysisResultFromHistory(historyData.analysis),
      };
    }

    throw new Error(historyData.detail || 'Failed to retrieve analysis results');
  }, [reconstructAnalysisResultFromHistory]);

  const recoverInterruptedUploadFromHistory = useCallback(async (elementType) => {
    const headers = { ...authService.getAuthHeader() };
    const maxAttempts = 18;
    const recoveryWindowMs = 5 * 60 * 1000;

    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      const response = await fetch(`${API_URL}/api/v1/video-history?limit=8&offset=0`, {
        headers,
      });

      if (!response.ok) {
        throw new Error(`Recovery lookup failed with HTTP ${response.status}`);
      }

      const data = await response.json();
      const historyItems = Array.isArray(data.history) ? data.history : [];
      const now = Date.now();

      const recoveredItem = historyItems.find((item) => {
        if (!item || item.element_type !== elementType || !item.video_id || !item.created_at) {
          return false;
        }

        const createdAtMs = Date.parse(item.created_at);
        if (Number.isNaN(createdAtMs)) {
          return false;
        }

        return (now - createdAtMs) <= recoveryWindowMs;
      });

      if (recoveredItem?.video_id) {
        return recoveredItem.video_id;
      }

      if (attempt < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, 5000));
      }
    }

    return null;
  }, []);

  // Derived: comparison viewer is active when both videos are loaded
  const compareActive = !!(compareVideoA && compareVideoB);

  // Cleanup compare blob URLs on unmount
  useEffect(() => {
    return () => {
      if (compareVideoA?.videoUrl?.startsWith('blob:')) URL.revokeObjectURL(compareVideoA.videoUrl);
      if (compareVideoB?.videoUrl?.startsWith('blob:')) URL.revokeObjectURL(compareVideoB.videoUrl);
    };
  }, [compareVideoA, compareVideoB]);

  // Element options for figure skating
  const elements = [
    // Triple Jumps (Olympic Level - Primary Focus)
    { value: 'jump_triple_axel', label: 'Triple Axel (3A) ⭐' },
    { value: 'jump_triple_lutz', label: 'Triple Lutz (3Lz)' },
    { value: 'jump_triple_flip', label: 'Triple Flip (3F)' },
    { value: 'jump_triple_loop', label: 'Triple Loop (3Lo)' },
    { value: 'jump_triple_salchow', label: 'Triple Salchow (3S)' },
    { value: 'jump_triple_toe_loop', label: 'Triple Toe Loop (3T)' },

    // Quadruple Jumps (Elite Level - Highest difficulty)
    { value: 'jump_quad_axel', label: 'Quad Axel (4A) ⭐⭐⭐' },
    { value: 'jump_quad_lutz', label: 'Quad Lutz (4Lz) ⭐⭐' },
    { value: 'jump_quad_flip', label: 'Quad Flip (4F) ⭐⭐' },
    { value: 'jump_quad_loop', label: 'Quad Loop (4Lo) ⭐' },
    { value: 'jump_quad_salchow', label: 'Quad Salchow (4S) ⭐' },
    { value: 'jump_quad_toe_loop', label: 'Quad Toe Loop (4T) ⭐' },

    // Jump Combinations (Olympic Level)
    { value: 'combo_3f_3t', label: 'Combo: Triple Flip + Triple Toe Loop (3F+3T) ⭐' },

    // Double Axel (Advanced Level)
    { value: 'jump_double_axel', label: 'Double Axel (2A)' },

    // Double Jumps
    { value: 'jump_double_flip', label: 'Double Flip (2F)' },
    { value: 'jump_double_lutz', label: 'Double Lutz (2Lz)' },
    { value: 'jump_double_loop', label: 'Double Loop (2Lo)' },
    { value: 'jump_double_salchow', label: 'Double Salchow (2S)' },
    { value: 'jump_double_toe_loop', label: 'Double Toe Loop (2T)' },

    // Single Jumps
    { value: 'jump_single_flip', label: 'Single Flip (1F)' },
    { value: 'jump_single_lutz', label: 'Single Lutz (1Lz)' },
    { value: 'jump_single_loop', label: 'Single Loop (1Lo)' },
    { value: 'jump_single_salchow', label: 'Single Salchow (1S)' },
    { value: 'jump_single_toe_loop', label: 'Single Toe Loop (1T)' },

    // Spins
    { value: 'spin_camel', label: 'Spin - Camel (CSp)' },
    { value: 'spin_sit', label: 'Spin - Sit (SSp)' },
    { value: 'spin_upright', label: 'Spin - Upright (USp)' },
    { value: 'spin_layback', label: 'Spin - Layback (LSp)' },
    { value: 'spin_combination', label: 'Spin - Combination (CoSp)' },
    { value: 'spin_change_foot_combination', label: 'Spin - Change Foot Combination (CCoSp) ⭐' },
    { value: 'spin_flying_sit', label: 'Spin - Flying Sit (FSSp) ⭐' }
  ];

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      // Read video duration from metadata
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        const duration = video.duration;
        setVideoPreview({ file, previewUrl, duration });
        const MAX_VIDEO_DURATION_SECONDS = 20;
        setUploadStatus(duration > MAX_VIDEO_DURATION_SECONDS ? 'too_long' : 'ready');
        video.src = '';  // Release media resource
      };
      video.onerror = () => {
        // Fallback: proceed without duration check
        setVideoPreview({ file, previewUrl, duration: null });
        setUploadStatus('ready');
      };
      video.src = previewUrl;
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'video/*': ['.mp4', '.mov', '.avi', '.webm']
    },
    maxFiles: 1,
    maxSize: 100 * 1024 * 1024, // 100MB
    disabled: uploadStatus === 'uploading' || uploadStatus === 'processing'
  });

  const handleAnalyze = useCallback(async () => {
    if (!videoPreview?.file || !selectedElement) {
      alert('Please select a video file and element type');
      return;
    }

    setUploadStatus('uploading');
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', videoPreview.file);
      formData.append('element_type', selectedElement);

      // Notify parent component that analysis is starting
      if (onAnalysisStart) {
        onAnalysisStart(selectedElement);
      }

      // Funnel: video analysis is the key activation event
      trackEvent('video_analysis_start', { element_type: selectedElement });

      // Upload and analyze video
      const response = await fetch(`${API_URL}/api/v1/analyze-video`, {
        method: 'POST',
        headers: {
          ...authService.getAuthHeader(),
        },
        body: formData,
      });

      const result = await response.json();

      if (result.success) {
        setVideoId(result.video_id);

        // Notify parent component that video is loaded
        if (onVideoLoaded) {
          onVideoLoaded(result.video_id);
        }

        setUploadStatus('processing');
        // Don't hardcode progress - let polling update it from backend
        setProcessingMessage('Video uploaded successfully, starting analysis...');

        // Start polling for status updates immediately
        pollAnalysisStatus(result.video_id);
      } else {
        throw new Error(result.error || 'Analysis failed');
      }
    } catch (error) {
      console.error('Analysis failed:', error);

      const message = String(error?.message || '').toLowerCase();
      const isRecoverableUploadFailure =
        message.includes('load failed') ||
        message.includes('networkerror') ||
        message.includes('failed to fetch') ||
        message.includes('network request failed');

      if (isRecoverableUploadFailure) {
        setUploadStatus('processing');
        setProcessingMessage('Upload response interrupted, checking whether analysis started...');

        try {
          const recoveredVideoId = await recoverInterruptedUploadFromHistory(selectedElement);
          if (recoveredVideoId) {
            setVideoId(recoveredVideoId);
            if (onVideoLoaded) {
              onVideoLoaded(recoveredVideoId);
            }
            setProcessingMessage('Upload recovered, loading analysis...');
            pollAnalysisStatus(recoveredVideoId);
            return;
          }
        } catch (recoveryError) {
          console.warn('⚠️ Upload recovery attempt failed:', recoveryError);
        }
      }

      setUploadStatus('error');
      setProcessingMessage(`Analysis failed: ${error.message}`);
    }
  }, [onAnalysisStart, onVideoLoaded, recoverInterruptedUploadFromHistory, selectedElement, videoPreview]);

  const pollAnalysisStatus = async (videoId) => {
    try {
      const response = await fetch(`${API_URL}/api/v1/video-status/${videoId}`, {
        headers: {
          ...authService.getAuthHeader(),
        },
      });
      const status = await response.json();

      setUploadProgress(status.progress || 0);
      setProcessingMessage(status.message || 'Processing...');

      if (status.status === 'complete') {
        setUploadStatus('complete');
        setUploadProgress(1);
        setProcessingMessage('Analysis complete, loading results...');

        // Invalidate video history cache so new video appears
        setVideoHistoryCache(null);

        try {
          const recovered = await fetchAnalysisResultWithRecovery(videoId);
          const result = recovered.result;

          console.log('📊 Analysis result received:', result);
          console.log('📊 Result source:', recovered.source);
          console.log('📊 Biomechanical data:', result.biomechanical_data);
          console.log('📊 Jump metrics:', result.biomechanical_data?.jump_metrics);
          console.log('📊 Spin metrics:', result.biomechanical_data?.spin_metrics);
          setAnalysisResult(result);

          if (result.fps && result.fps > 0 && result.fps < 240) {
            setVideoFps(result.fps);
          }
          setOverlayVideoUrl(null);
          setIsGeneratingOverlay(false);
          setShowComparisonPanel(true);

          const correctFrameCount = result.total_video_frames || result.frame_count || 0;
          setProcessingMessage('Analysis loaded, preparing video...');
          await autoGenerateOverlayVideo(videoId, correctFrameCount);

          if (onAnalysisComplete) {
            onAnalysisComplete(result);
          }
        } catch (error) {
          console.error('❌ Error getting analysis result:', error);
          setUploadStatus('complete');
          setProcessingMessage('Analysis complete, but results are still syncing. Check Video History.');
        }
      } else if (status.status === 'error') {
        setUploadStatus('error');
        setProcessingMessage(status.message || 'Analysis failed');
      } else {
        // Continue polling every 1 second for responsive progress updates
        setTimeout(() => pollAnalysisStatus(videoId), 1000);
      }
    } catch (error) {
      console.error('Status polling failed:', error);
      setUploadStatus('error');
      setProcessingMessage('Failed to get analysis status');
    }
  };

  // Auto-generate overlay video after analysis completes
  const autoGenerateOverlayVideo = async (videoId, frameCount) => {
    try {
      console.log('🎬 Starting overlay video auto-generation...');
      setIsGeneratingOverlay(true);

      // Start generation
      const response = await fetch(`${API_URL}/api/v1/generate-overlay-video/${videoId}`, {
        method: 'POST',
        headers: {
          ...authService.getAuthHeader(),
        },
      });

      if (!response.ok) {
        console.warn(`⚠️ Overlay generation request failed with status ${response.status}`);
        setIsGeneratingOverlay(false);
        // Fall back to canvas overlay for ANY video on HTTP error
        if (frameCount > 0) {
          console.log('🎨 Falling back to canvas overlay after HTTP error');
          setUseCanvasOverlay(true);
          setTotalFrameCount(frameCount);
        }
        return;
      }

      const result = await response.json();
      console.log('📊 Generation response:', result);

      if (result.status === 'skipped' && result.use_canvas_overlay) {
        // Large video - use client-side canvas overlay instead
        console.log(`🎨 Using canvas overlay for large video (${result.frame_count} frames)`);
        setUseCanvasOverlay(true);
        // Prefer frameCount from analysis result (total video frames) over overlay endpoint's frame_count (pose count)
        setTotalFrameCount(frameCount || result.frame_count);
        setIsGeneratingOverlay(false);
        return;
      } else if (result.status === 'ready') {
        // Already exists
        console.log('✅ Overlay video already exists:', result.overlay_video_url);
        setOverlayVideoUrl(result.overlay_video_url);
        setIsGeneratingOverlay(false);
      } else if (result.status === 'processing') {
        // Wait for generation to complete
        console.log('⏳ Waiting for overlay video generation...');
        await waitForOverlayVideoGeneration(videoId, frameCount);
      } else {
        // Unrecognized status - fall back to canvas overlay
        console.warn(`⚠️ Unrecognized overlay status: ${result.status}`);
        setIsGeneratingOverlay(false);
        if (frameCount > 0) {
          console.log('🎨 Falling back to canvas overlay for unrecognized status');
          setUseCanvasOverlay(true);
          setTotalFrameCount(frameCount);
        }
      }
    } catch (error) {
      console.error('Failed to generate overlay video:', error);
      setIsGeneratingOverlay(false);
      // Fall back to canvas overlay on exception
      if (frameCount > 0) {
        console.log('🎨 Falling back to canvas overlay after exception');
        setUseCanvasOverlay(true);
        setTotalFrameCount(frameCount);
      }
    }
  };

  // Wait for overlay video generation to complete
  const waitForOverlayVideoGeneration = async (videoId, frameCount) => {
    const maxAttempts = 60; // Max 2 minutes
    let attempts = 0;

    while (attempts < maxAttempts) {
      try {
        const response = await fetch(`${API_URL}/api/v1/overlay-video-status/${videoId}`, {
          headers: {
            ...authService.getAuthHeader(),
          },
        });
        const result = await response.json();
        console.log(`📊 Status check #${attempts + 1}:`, result);

        if (result.status === 'ready') {
          console.log('✅ Overlay video ready!');
          console.log('📹 Video URL:', result.overlay_video_url);
          console.log('💾 File size:', result.file_size_mb, 'MB');

          // Verify the video is actually ready by checking file size
          if (result.file_size_mb && result.file_size_mb > 0) {
            // Add small delay to ensure file is fully flushed to disk
            console.log('⏳ Waiting 1 second for file to be fully ready...');
            await new Promise(resolve => setTimeout(resolve, 1000));

            console.log('✅ Setting overlay video URL:', result.overlay_video_url);
            console.log('📍 Full URL will be:', `${API_URL}${result.overlay_video_url}`);
            setOverlayVideoUrl(result.overlay_video_url);
            setIsGeneratingOverlay(false);
            console.log('✅ State updated - player will now show');
            return;
          } else {
            console.warn('⚠️ Video file exists but has no size, continuing to wait...');
            // Continue waiting
          }
        } else if (result.status === 'skipped' && result.use_canvas_overlay) {
          // Server decided to use canvas overlay (e.g., >150 frames detected on cold start)
          console.log(`🎨 Server returned skipped during polling, switching to canvas overlay`);
          setUseCanvasOverlay(true);
          setTotalFrameCount(frameCount || result.frame_count);
          setIsGeneratingOverlay(false);
          return;
        } else if (result.status === 'not_found' && result.use_canvas_overlay) {
          // Cold start: overlay not available, server suggests canvas overlay
          console.log('🎨 Canvas overlay suggested by server (overlay not available)');
          setUseCanvasOverlay(true);
          setTotalFrameCount(frameCount || result.frame_count);
          setIsGeneratingOverlay(false);
          return;
        } else if (result.status === 'failed') {
          // Generation failed - fall back to canvas overlay
          console.warn('❌ Overlay generation failed:', result.error);
          setIsGeneratingOverlay(false);
          if (frameCount > 0) {
            console.log('🎨 Falling back to canvas overlay after generation failure');
            setUseCanvasOverlay(true);
            setTotalFrameCount(frameCount);
          }
          return;
        } else {
          console.log(`⏳ Status: ${result.status}, continuing to wait...`);
        }

        // Wait 2 seconds before next check
        await new Promise(resolve => setTimeout(resolve, 2000));
        attempts++;
      } catch (error) {
        console.error('Failed to check overlay video status:', error);
        setIsGeneratingOverlay(false);
        // Fall back to canvas overlay on polling error
        if (frameCount > 0) {
          console.log('🎨 Falling back to canvas overlay after polling error');
          setUseCanvasOverlay(true);
          setTotalFrameCount(frameCount);
        }
        return; // Exit on error
      }
    }

    console.warn('⏱️ Overlay video generation timeout after 2 minutes');
    // Fall back to canvas overlay using passed frameCount (not stale analysisResult)
    if (frameCount > 0) {
      console.log('🎨 Timeout fallback: switching to canvas overlay');
      setUseCanvasOverlay(true);
      setTotalFrameCount(frameCount);
    }
    setIsGeneratingOverlay(false);
  };

  // Pre-load coaching map from bulk frames for SynchronizedPosePlayer path
  // (CanvasPoseOverlayPlayer includes coaching in poseData directly)
  const loadCoachingMap = useCallback(async () => {
    if (!videoId) return;
    try {
      const resp = await fetch(`${API_URL}/api/v1/videos/${videoId}/frames`, {
        headers: { ...authService.getAuthHeader() },
      });
      if (!resp.ok) return;
      const data = await resp.json();
      const map = {};
      for (const f of (data.frames || [])) {
        if (f.coaching) {
          map[f.frame_number] = f.coaching;
        }
      }
      setCoachingMap(map);
    } catch (e) {
      console.warn('Failed to load coaching map:', e);
    }
  }, [videoId]);

  useEffect(() => {
    if (!videoId || uploadStatus !== 'complete') {
      return;
    }
    loadCoachingMap();
  }, [videoId, uploadStatus, loadCoachingMap]);

  // Handle frame changes from synchronized player
  const handleFrameChange = useCallback((frameNumber, poseData) => {
    setCurrentFrame(frameNumber);
    setFrameCoaching(poseData?.coaching || coachingMap?.[frameNumber] || null);
  }, [coachingMap]);

  // Handle action step clicks
  const handleActionStepClick = (actionStep, frameNumber) => {
    console.log(`Action step clicked for frame ${frameNumber}:`, actionStep);
    // Could implement detailed action step explanations or navigation
  };

  // Handle bookmarking moments
  const handleBookmark = (frameNumber, timestamp) => {
    console.log(`Bookmark added at frame ${frameNumber} (${timestamp}s)`);
    // Could implement bookmark storage and retrieval
  };

  // Handle phase correction — re-run analysis with user-corrected air phase
  const handlePhaseCorrection = useCallback(async (airStart, airEnd) => {
    if (!videoId) return;
    setIsRecalculating(true);
    try {
      const headers = {
        'Content-Type': 'application/json',
        ...authService.getAuthHeader(),
      };
      const response = await fetch(`${API_URL}/api/v1/videos/${videoId}/correct-phases`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ air_start: airStart, air_end: airEnd }),
      });
      const data = await response.json();
      if (!response.ok) {
        console.error('Phase correction failed:', data);
        return;
      }
      // Re-fetch analysis result and coaching map in parallel
      const [recovered] = await Promise.all([
        fetchAnalysisResultWithRecovery(videoId),
        loadCoachingMap(),
      ]);
      // Update the displayed analysis result with corrected data
      if (recovered && recovered.result) {
        setAnalysisResult(recovered.result);
      }
      setIsPhaseCorrected(true);
    } catch (err) {
      console.error('Phase correction error:', err);
    } finally {
      setIsRecalculating(false);
    }
  }, [videoId, fetchAnalysisResultWithRecovery, loadCoachingMap]);

  const resetUpload = () => {
    // Clean up blob URLs before resetting
    if (overlayVideoUrl && overlayVideoUrl.startsWith('blob:')) {
      URL.revokeObjectURL(overlayVideoUrl);
    }
    if (videoPreview?.previewUrl) {
      URL.revokeObjectURL(videoPreview.previewUrl);
    }

    setUploadStatus('idle');
    setUploadProgress(0);
    setSelectedElement('');
    setAnalysisResult(null);
    setVideoPreview(null);
    setProcessingMessage('');
    setVideoId(null);

    // Reset synchronized features state
    setCurrentFrame(0);
    setFrameCoaching(null);
    setIsPhaseCorrected(false);
    setIsRecalculating(false);

    // Reset overlay video state
    setOverlayVideoUrl(null);
    setIsGeneratingOverlay(false);

    // Reset canvas overlay state
    setUseCanvasOverlay(false);
    setTotalFrameCount(0);
    setVideoFps(30);

    // Reset history viewing state
    setViewingHistoryVideo(false);
    setHistoryVideoData(null);

    // Reset comparison panel state
    setShowComparisonPanel(false);
  };

  // Switch to compare mode with current video pre-filled as slot A
  const compareThisVideo = async () => {
    if (!videoId || !selectedElement) return;
    // Fetch thumbnail before resetting state (resetUpload clears videoId)
    let thumbUrl = null;
    try {
      const res = await fetch(`${API_URL}/api/v1/thumbnail/${videoId}`, {
        headers: { ...authService.getAuthHeader() },
      });
      if (res.ok) {
        const blob = await res.blob();
        thumbUrl = URL.createObjectURL(blob);
      }
    } catch (e) { /* thumbnail optional */ }
    const slot = {
      videoId,
      elementType: selectedElement,
      label: selectedElement.replace('_', ' ').toUpperCase(),
      date: new Date().toLocaleDateString(),
      thumbnailUrl: thumbUrl,
      frameCount: totalFrameCount || analysisResult?.frame_count || 0,
    };
    // Reset single analysis state (also revokes overlayVideoUrl and videoPreview)
    resetUpload();
    // Pre-fill slot A and switch to compare mode
    setCompareSlotA(slot);
    setCompareSlotB(null);
    setCompareVideoA(null);
    setCompareVideoB(null);
    setMode('compare');
  };

  // Compare mode handlers
  const handleCompareSelect = (videoId, elementType, date, thumbnailUrl, frameCount) => {
    const slot = { videoId, elementType, label: elementType.replace('_', ' ').toUpperCase(), date, thumbnailUrl, frameCount };
    if (!compareSlotA) {
      setCompareSlotA(slot);
    } else if (!compareSlotB) {
      setCompareSlotB(slot);
    }
    // Both slots full - ignore
  };

  const handleClearSlot = (slotKey) => {
    if (slotKey === 'A') setCompareSlotA(null);
    else setCompareSlotB(null);
  };

  const handleCompareStart = async () => {
    if (!compareSlotA || !compareSlotB) return;

    // Load both videos' data
    const loadVideoData = async (slot) => {
      // Fetch analysis data
      const res = await fetch(`${API_URL}/api/v1/video-history/${slot.videoId}`, {
        headers: { ...authService.getAuthHeader() },
      });
      const data = await res.json();
      if (!data.success) throw new Error(`Failed to load ${slot.videoId}`);
      const analysis = data.analysis;

      let biomechData = analysis.biomechanical_results;
      if (typeof biomechData === 'string') {
        try { biomechData = JSON.parse(biomechData); } catch { biomechData = {}; }
      }

      const frameCount = analysis.frame_count || 0;

      // Check if burn-in overlay actually exists
      let hasOverlay = false;
      try {
        const existsRes = await fetch(`${API_URL}/api/v1/overlay-video/${slot.videoId}/exists`, {
          headers: { ...authService.getAuthHeader() },
        });
        if (existsRes.ok) {
          const existsData = await existsRes.json();
          hasOverlay = existsData.exists === true;
        }
      } catch (e) {
        console.warn(`⚠️ Overlay existence check failed for ${slot.videoId}:`, e.message);
      }

      let videoUrl;
      const videoRes = await fetch(`${API_URL}/api/v1/history-video/${slot.videoId}/overlay`, {
        headers: { ...authService.getAuthHeader() },
      });
      if (videoRes.ok) {
        const blob = await videoRes.blob();
        videoUrl = URL.createObjectURL(blob);
      }

      return {
        videoId: slot.videoId,
        videoUrl: videoUrl,
        elementType: analysis.element_type,
        totalFrames: frameCount,
        fps: analysis.fps || 30,
        hasOverlay: hasOverlay,
        analysisData: {
          frame_count: frameCount,
          biomechanical_data: biomechData,
          element_type: analysis.element_type,
        },
      };
    };

    try {
      const [vA, vB] = await Promise.all([
        loadVideoData(compareSlotA),
        loadVideoData(compareSlotB),
      ]);
      setCompareVideoA(vA);
      setCompareVideoB(vB);
    } catch (error) {
      console.error('Failed to load comparison videos:', error);
      alert('Failed to load one or both videos. Please try again.');
    }
  };

  const handleCompareClose = () => {
    // Cleanup blob URLs
    if (compareVideoA?.videoUrl?.startsWith('blob:')) URL.revokeObjectURL(compareVideoA.videoUrl);
    if (compareVideoB?.videoUrl?.startsWith('blob:')) URL.revokeObjectURL(compareVideoB.videoUrl);
    // Cleanup thumbnail blob URLs from slots
    if (compareSlotA?.thumbnailUrl?.startsWith('blob:')) URL.revokeObjectURL(compareSlotA.thumbnailUrl);
    if (compareSlotB?.thumbnailUrl?.startsWith('blob:')) URL.revokeObjectURL(compareSlotB.thumbnailUrl);
    setCompareVideoA(null);
    setCompareVideoB(null);
  };

  const loadVideoFromHistory = async (historyVideoId) => {
    try {
      console.log('🔄 Loading video from history:', historyVideoId);

      // Clean up previous blob URL if exists
      if (overlayVideoUrl && overlayVideoUrl.startsWith('blob:')) {
        URL.revokeObjectURL(overlayVideoUrl);
      }

      // Reset all relevant states at the start
      setIsGeneratingOverlay(false);
      setOverlayVideoUrl(null);  // Clear any previous URL
      setAnalysisResult(null);
      setViewingHistoryVideo(false);
      setHistoryVideoData(null);

      setUploadStatus('processing');
      setProcessingMessage('Loading video from history...');

      // Fetch the video analysis data from history
      const response = await fetch(`${API_URL}/api/v1/video-history/${historyVideoId}`, {
        headers: {
          ...authService.getAuthHeader(),
        },
      });
      const data = await response.json();

      if (!data.success) {
        throw new Error('Failed to load video from history');
      }

      const analysis = data.analysis;
      console.log('📊 Loaded history analysis:', analysis);
      console.log('📹 Overlay path in DB:', analysis.overlay_video_path);
      console.log('📹 Original path in DB:', analysis.original_video_path);

      // Parse biomechanical results if they're stored as JSON string
      let biomechData = analysis.biomechanical_results;
      if (typeof biomechData === 'string') {
        try {
          biomechData = JSON.parse(biomechData);
        } catch (e) {
          console.warn('⚠️ Failed to parse biomechanical data:', e);
          biomechData = {};
        }
      }

      // Reconstruct analysis result format
      const reconstructedResult = {
        video_id: historyVideoId,
        element_type: analysis.element_type,
        processing_time: analysis.processing_time,
        confidence_score: analysis.confidence_score,
        frame_count: analysis.frame_count || 0,  // Add frame count from database
        pose_data: [], // Pose data is in frame_storage.db, linked by video_id
        biomechanical_data: biomechData,
        coaching_feedback: analysis.coaching_feedback,
        created_at: analysis.created_at
      };

      const frameCount = analysis.frame_count || 0;

      // Set analysis result FIRST so data is shown even if video fetch fails
      setVideoId(historyVideoId);
      setSelectedElement(analysis.element_type);
      setViewingHistoryVideo(true);
      setHistoryVideoData(analysis);
      setAnalysisResult(reconstructedResult);
      setShowComparisonPanel(true);

      // Set FPS from history database (if available) for frame synchronization
      if (analysis.fps && analysis.fps > 0 && analysis.fps < 240) {
        setVideoFps(analysis.fps);
      }

      // Check if burn-in overlay video actually exists (not frame-count heuristic)
      let overlayExists = false;
      try {
        const existsRes = await fetch(`${API_URL}/api/v1/overlay-video/${historyVideoId}/exists`, {
          headers: { ...authService.getAuthHeader() },
        });
        if (existsRes.ok) {
          const existsData = await existsRes.json();
          overlayExists = existsData.exists === true;
        }
      } catch (e) {
        console.warn('⚠️ Overlay existence check failed, falling back to canvas overlay:', e.message);
      }
      console.log(`📹 Overlay exists for ${historyVideoId}: ${overlayExists} (${frameCount} frames)`);

      if (!overlayExists && frameCount > 0) {
        // No burn-in overlay → use canvas overlay for skeleton rendering
        console.log('🎨 No burn-in overlay video, using canvas overlay for skeleton');
        setUseCanvasOverlay(true);
        setTotalFrameCount(frameCount);
        setUploadStatus('complete');
        setProcessingMessage('Analysis loaded with canvas overlay');
        return;
      }

      // Notify parent component that video is loaded
      if (onVideoLoaded) {
        onVideoLoaded(historyVideoId);
      }

      // Burn-in overlay exists → fetch and use SynchronizedPosePlayer
      setProcessingMessage('Loading video file...');
      const videoApiUrl = `${API_URL}/api/v1/history-video/${historyVideoId}/overlay`;
      console.log('🎬 Fetching burn-in overlay video:', videoApiUrl);

      try {
        const videoResponse = await fetch(videoApiUrl, {
          headers: {
            ...authService.getAuthHeader(),
          },
        });

        if (!videoResponse.ok) {
          // Video fetch failed but analysis data is already set
          console.warn(`⚠️ Video file unavailable: ${videoResponse.status} ${videoResponse.statusText}`);
          setOverlayVideoUrl(null);  // Mark video as unavailable
          setUploadStatus('complete');
          setProcessingMessage('Analysis loaded (video unavailable)');
          console.log('✅ Analysis data loaded, but video file is unavailable');
          return;
        }

        const videoBlob = await videoResponse.blob();
        const videoBlobUrl = URL.createObjectURL(videoBlob);
        console.log('🎬 Video blob URL created:', videoBlobUrl);
        console.log('🎬 Video blob size:', (videoBlob.size / (1024 * 1024)).toFixed(2), 'MB');

        setOverlayVideoUrl(videoBlobUrl);  // Set the blob URL (not API URL)
        setUploadStatus('complete');
        setProcessingMessage('Video loaded from history!');

        // Log final state values for debugging
        console.log('✅ History video loaded successfully');
        console.log('📊 Final state values:');
        console.log('  - videoId:', historyVideoId);
        console.log('  - overlayVideoUrl:', videoBlobUrl);
        console.log('  - isGeneratingOverlay:', false);
        console.log('  - uploadStatus:', 'complete');
        console.log('  - analysisResult:', reconstructedResult);
      } catch (videoError) {
        // Video fetch error but analysis data is already set
        console.warn('⚠️ Could not load video file:', videoError.message);
        setOverlayVideoUrl(null);
        setUploadStatus('complete');
        setProcessingMessage('Analysis loaded (video unavailable)');
        console.log('✅ Analysis data loaded, but video file could not be fetched');
      }

    } catch (error) {
      console.error('❌ Failed to load video from history:', error);
      setUploadStatus('error');
      setProcessingMessage(`Failed to load analysis: ${error.message}`);
    }
  };

  const getStatusIcon = () => {
    switch (uploadStatus) {
      case 'uploading':
      case 'processing':
        return <Clock className="w-5 h-5 animate-spin" />;
      case 'complete':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'error':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Film className="w-5 h-5" />;
    }
  };

  return (
    <div className="video-analysis-upload">

      {/* Mode Switch - only show when idle */}
      {(uploadStatus === 'idle' || uploadStatus === 'ready') && (
        <div className="mode-switch">
          <button
            className={`mode-btn ${mode === 'single' ? 'active' : ''}`}
            onClick={() => setMode('single')}
          >
            Single Analysis
          </button>
          <button
            className={`mode-btn ${mode === 'compare' ? 'active' : ''}`}
            onClick={() => setMode('compare')}
          >
            Compare
          </button>
        </div>
      )}

      {/* Upload Area - single mode only */}
      {mode === 'single' && (uploadStatus === 'idle' || uploadStatus === 'ready') && (
        <VideoUploadSection
          getRootProps={getRootProps}
          getInputProps={getInputProps}
          isDragActive={isDragActive}
          videoPreview={videoPreview}
          selectedElement={selectedElement}
          setSelectedElement={setSelectedElement}
          elements={elements}
          handleAnalyze={handleAnalyze}
          resetUpload={resetUpload}
          uploadStatus={uploadStatus}
        />
      )}

      {/* Processing Status */}
      {(uploadStatus === 'uploading' || uploadStatus === 'processing' || (uploadStatus === 'complete' && !analysisResult)) && (
        <AnalysisStatusSection processingMessage={processingMessage} />
      )}

      {/* Results - Enhanced with Synchronized Features */}
      {uploadStatus === 'complete' && analysisResult && (
        <div className="analysis-results">
          {/* Video Player FIRST - maximizes video display area */}
          <VideoPlayerSection
            isGeneratingOverlay={isGeneratingOverlay}
            useCanvasOverlay={useCanvasOverlay}
            overlayVideoUrl={overlayVideoUrl}
            uploadStatus={uploadStatus}
            viewingHistoryVideo={viewingHistoryVideo}
            videoId={videoId}
            videoPreview={videoPreview}
            totalFrameCount={totalFrameCount}
            videoFps={videoFps}
            analysisResult={analysisResult}
            selectedElement={selectedElement}
            currentFrame={currentFrame}
            frameCoaching={frameCoaching}
            handleFrameChange={handleFrameChange}
            handleBookmark={handleBookmark}
            handleActionStepClick={handleActionStepClick}
            onCorrectPhase={handlePhaseCorrection}
            isRecalculating={isRecalculating}
            isCorrected={isPhaseCorrected}
          />

          {/* Analysis details below video */}
          <div className="analysis-header-line">
            <span className="header-item"><strong>Element:</strong> {analysisResult.element_type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
            <span className="header-divider">|</span>
            <span className="header-item"><strong>Processing Time:</strong> {analysisResult.processing_time != null ? analysisResult.processing_time.toFixed(1) + 's' : 'N/A'}</span>
            <span className="header-divider">|</span>
            <span className="header-item"><strong>Confidence Score:</strong> {analysisResult.confidence_score != null ? (analysisResult.confidence_score * 100).toFixed(1) + '%' : 'N/A'}</span>
            <span className="header-divider">|</span>
            <span className="header-item"><strong>Frames Analyzed:</strong> {analysisResult.frame_count || analysisResult.pose_data?.length || 'N/A'}</span>
          </div>

          <div className="key-measurements-full">
            <h5 className="measurements-title">Key Measurements</h5>
            <KeyMeasurementsDisplay analysisResult={analysisResult} />
          </div>

          {analysisResult.coaching_feedback && (
            <div className="coaching-feedback">
              <h5>Coach Feedback</h5>
              <div className="feedback-content">
                <ReactMarkdown
                  components={{
                    code({ node, inline, className, children, ...props }) {
                      const match = /language-(\w+)/.exec(className || '')
                      return !inline && match ? (
                        <SyntaxHighlighter
                          style={tomorrow}
                          language={match[1]}
                          PreTag="div"
                          {...props}
                        >
                          {String(children).replace(/\n$/, '')}
                        </SyntaxHighlighter>
                      ) : (
                        <code className={className} {...props}>
                          {children}
                        </code>
                      )
                    }
                  }}
                >
                  {analysisResult.coaching_feedback}
                </ReactMarkdown>
              </div>
            </div>
          )}

          {/* Comparison Panel */}
          {showComparisonPanel && videoId && selectedElement && (
            <ComparisonPanel
              videoId={videoId}
              elementType={selectedElement}
            />
          )}

          <div className="analysis-action-buttons">
            <button onClick={resetUpload} className="new-analysis-button">
              Analyze Another Video
            </button>
            <button onClick={compareThisVideo} className="new-analysis-button compare-this-btn">
              Compare This Video
            </button>
          </div>
        </div>
      )}

      {/* Error State */}
      {uploadStatus === 'error' && (
        <div className="error-state">
          <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
          <p className="error-message">{processingMessage}</p>
          <button onClick={resetUpload} className="retry-button">
            Try Again
          </button>
        </div>
      )}

      {/* Video History - single mode */}
      {mode === 'single' && uploadStatus === 'idle' && (
        <VideoHistory
          onVideoSelect={loadVideoFromHistory}
          cache={videoHistoryCache}
          setCache={setVideoHistoryCache}
        />
      )}

      {/* Compare Mode */}
      {mode === 'compare' && (uploadStatus === 'idle' || uploadStatus === 'ready') && !compareActive && (
        <>
          <VideoComparisonSetup
            slotA={compareSlotA}
            slotB={compareSlotB}
            onClearSlot={handleClearSlot}
            onCompareStart={handleCompareStart}
          />
          <VideoHistory
            onVideoSelect={loadVideoFromHistory}
            cache={videoHistoryCache}
            setCache={setVideoHistoryCache}
            compareMode={true}
            onCompareSelect={handleCompareSelect}
            filterElementType={compareSlotA ? compareSlotA.elementType : null}
          />
        </>
      )}

      {/* Compare Viewer */}
      {mode === 'compare' && compareActive && (
        <VideoComparisonViewer
          videoA={compareVideoA}
          videoB={compareVideoB}
          fpsA={compareVideoA?.fps || 30}
          fpsB={compareVideoB?.fps || 30}
          onClose={handleCompareClose}
        />
      )}

      <style>{`
        .video-analysis-upload {
          background: white;
          border-radius: 12px;
          padding: 20px;
          margin: 16px 0;
          border: 1px solid #e5e7eb;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .mode-switch {
          display: flex;
          gap: 0;
          margin-bottom: 16px;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          overflow: hidden;
          width: fit-content;
        }
        .mode-btn {
          padding: 8px 20px;
          border: none;
          background: #f9fafb;
          font-size: 14px;
          font-weight: 500;
          color: #6b7280;
          cursor: pointer;
          transition: all 0.2s;
        }
        .mode-btn.active {
          background: #3b82f6;
          color: white;
        }
        .mode-btn:hover:not(.active) {
          background: #f3f4f6;
        }

        .dropzone {
          border: 2px dashed #d1d5db;
          border-radius: 12px;
          padding: 24px 20px 20px;
          text-align: center;
          transition: all 0.3s ease;
          cursor: pointer;
          background: #fafafa;
        }

        .dropzone:hover {
          border-color: #3b82f6;
          background: #f0f9ff;
        }

        .dropzone:hover .upload-icon-wrapper {
          transform: translateY(-2px);
          background: rgba(59, 130, 246, 0.08);
          border-color: rgba(59, 130, 246, 0.3);
        }

        .dropzone:hover .upload-icon {
          color: #3b82f6;
        }

        .dropzone.dragactive {
          border-color: #3b82f6;
          background: #eff6ff;
        }

        .dropzone.has-file {
          border-color: #10b981;
          background: #f0fdf4;
        }

        .upload-icon-wrapper {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: rgba(0, 0, 0, 0.04);
          border: 1px solid rgba(0, 0, 0, 0.08);
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 10px;
          transition: all 0.3s ease;
        }

        .upload-icon {
          width: 22px;
          height: 22px;
          color: #9ca3af;
          transition: color 0.3s ease;
        }

        .drop-title {
          font-size: 15px;
          font-weight: 500;
          color: #374151;
          margin: 0 0 4px;
        }

        .drop-hint {
          font-size: 13px;
          color: #9ca3af;
          margin: 0;
        }

        .video-preview {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
        }

        .preview-video {
          border-radius: 8px;
          border: 1px solid #e5e7eb;
        }

        .file-name {
          font-weight: 500;
          margin: 0;
        }

        .file-size {
          color: #6b7280;
          font-size: 14px;
          margin: 0;
        }

        .element-selector {
          margin-top: 20px;
        }

        .element-select {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 14px;
          background: white;
        }

        .action-buttons {
          display: flex;
          gap: 12px;
          margin-top: 20px;
          justify-content: center;
        }

        .analyze-button {
          display: flex;
          align-items: center;
          justify-content: center;
          background: #3b82f6;
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 6px;
          font-weight: 500;
          cursor: pointer;
          transition: background 0.2s;
          min-height: 44px;
          touch-action: manipulation;
        }

        .analyze-button:hover:not(:disabled) {
          background: #2563eb;
        }

        .analyze-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .reset-button {
          display: flex;
          align-items: center;
          justify-content: center;
          background: #6b7280;
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 6px;
          font-weight: 500;
          cursor: pointer;
          transition: background 0.2s;
          min-height: 44px;
          touch-action: manipulation;
        }

        .reset-button:hover {
          background: #4b5563;
        }

        .canvas-overlay-badge {
          display: inline-block;
          padding: 2px 8px;
          font-size: 11px;
          font-weight: 500;
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          color: white;
          border-radius: 4px;
          margin-left: 8px;
          vertical-align: middle;
        }

        .processing-status {
          text-align: center;
          padding: 20px;
        }

        .progress-container {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 12px;
        }

        .progress-bar {
          flex: 1;
          height: 8px;
          background: #e5e7eb;
          border-radius: 4px;
          overflow: hidden;
        }

        .progress-fill {
          height: 100%;
          background: linear-gradient(90deg, #3b82f6 0%, #2563eb 50%, #3b82f6 100%);
          background-size: 200% 100%;
          background-position: 0% 0%;
          transition: width 0.8s ease-out;
          animation: shimmer 2s linear infinite;
        }

        @keyframes shimmer {
          0% {
            background-position: 200% 0%;
          }
          100% {
            background-position: -200% 0%;
          }
        }

        .progress-text {
          font-weight: 500;
          font-size: 14px;
          min-width: 40px;
        }

        .processing-message {
          color: #6b7280;
          font-size: 14px;
          margin: 0;
        }

        .analysis-results {
          text-align: center;
          padding: 4px 0;
        }

        /* Video Analysis Header - Single Line */}
        .analysis-header-line {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 12px 16px;
          background: #f9fafb;
          border-radius: 6px;
          margin-bottom: 16px;
          font-size: 14px;
          border: 1px solid #e5e7eb;
        }

        .header-item {
          white-space: nowrap;
        }

        .header-divider {
          color: #d1d5db;
          font-weight: 300;
        }

        @media (max-width: 768px) {
          .analysis-header-line {
            flex-direction: column;
            gap: 4px;
          }

          .header-divider {
            display: none;
          }
        }

        /* Key Measurements Panel - Full Width */
        .key-measurements-full {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-radius: 8px;
          padding: 20px;
          color: white;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          margin-bottom: 16px;
          width: 100%;
        }

        .measurements-title {
          margin: 0 0 16px 0;
          font-weight: 600;
          font-size: 16px;
          text-align: center;
          color: white;
        }

        .measurements-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 12px;
        }

        @media (max-width: 768px) {
          .measurements-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 480px) {
          .measurements-grid {
            grid-template-columns: 1fr 1fr;
            gap: 8px;
          }

          .key-measurements-full {
            padding: 12px 8px;
            margin: 0;
            width: 100%;
            border-radius: 0;
          }

          .measurements-title {
            font-size: 14px;
            margin-bottom: 12px;
          }

          .measurement-item {
            padding: 10px 8px;
            width: 100%;
          }

          .measurement-value {
            font-size: 16px;
          }

          .measurement-label {
            font-size: 10px;
          }

          .measurement-subtext {
            font-size: 9px;
          }

          .measurement-item.quality-badge {
            grid-column: span 1;
          }
        }

        .measurement-item {
          background: rgba(255, 255, 255, 0.15);
          border-radius: 6px;
          padding: 10px 12px;
          display: flex;
          flex-direction: column;
          gap: 4px;
          backdrop-filter: blur(10px);
          transition: all 0.2s;
        }

        .measurement-item:hover {
          background: rgba(255, 255, 255, 0.25);
          transform: translateY(-2px);
        }

        .measurement-item.highlight {
          background: rgba(255, 255, 255, 0.25);
          border: 1px solid rgba(255, 255, 255, 0.3);
          font-weight: 500;
        }

        .measurement-label {
          font-size: 11px;
          opacity: 0.9;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .measurement-value {
          font-size: 18px;
          font-weight: 700;
          letter-spacing: -0.5px;
        }

        .measurement-subtext {
          font-size: 11px;
          opacity: 0.8;
          font-weight: 400;
        }

        .measurement-item.quality-badge {
          grid-column: span 2;
          text-align: center;
        }

        .quality-level {
          display: inline-block;
          background: rgba(255, 255, 255, 0.3);
          padding: 6px 16px;
          border-radius: 20px;
          font-size: 13px;
          font-weight: 600;
          text-transform: capitalize;
          margin-top: 4px;
        }

        .quality-level.elite_triple_level,
        .quality-level.quad_capable,
        .quality-level.elite_quad_level {
          background: rgba(16, 185, 129, 0.3);
          border: 1px solid rgba(16, 185, 129, 0.5);
        }

        .quality-level.strong_triple {
          background: rgba(59, 130, 246, 0.3);
          border: 1px solid rgba(59, 130, 246, 0.5);
        }

        /* Combination Jump Specific Styles */
        .combo-header {
          text-align: center;
          font-size: 16px;
        }

        .combo-header .measurement-value {
          font-size: 24px;
          font-weight: 800;
        }

        .quality-level.excellent {
          background: rgba(16, 185, 129, 0.3);
          border: 1px solid rgba(16, 185, 129, 0.5);
        }

        .quality-level.good {
          background: rgba(59, 130, 246, 0.3);
          border: 1px solid rgba(59, 130, 246, 0.5);
        }

        .quality-level.needs_improvement,
        .quality-level.poor {
          background: rgba(239, 68, 68, 0.3);
          border: 1px solid rgba(239, 68, 68, 0.5);
        }

        .no-measurements {
          text-align: center;
          opacity: 0.8;
          font-size: 14px;
        }

        .coaching-feedback {
          background: #eff6ff;
          border-radius: 6px;
          padding: 16px;
          margin-bottom: 16px;
          text-align: left;
        }

        .coaching-feedback h5 {
          margin: 0 0 8px 0;
          font-weight: 600;
          color: #1e40af;
        }

        .feedback-content {
          font-size: 14px;
          line-height: 1.5;
          white-space: pre-wrap;
        }

        .new-analysis-button {
          background: #10b981;
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 6px;
          font-weight: 500;
          cursor: pointer;
          transition: background 0.2s;
          min-height: 44px;
          touch-action: manipulation;
        }

        .new-analysis-button:hover {
          background: #059669;
        }

        .analysis-action-buttons {
          display: flex;
          justify-content: center;
          gap: 12px;
          margin-top: 16px;
        }
        .compare-this-btn {
          background: #3b82f6;
        }
        .compare-this-btn:hover {
          background: #2563eb;
        }

        .error-state {
          text-align: center;
          padding: 20px;
        }

        .error-message {
          color: #dc2626;
          margin: 8px 0 16px 0;
        }

        .retry-button {
          background: #dc2626;
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 6px;
          font-weight: 500;
          cursor: pointer;
          transition: background 0.2s;
        }

        .retry-button:hover {
          background: #b91c1c;
        }

        /* Synchronized Features Styles */
        .synchronized-features {
          margin-top: 4px;
          padding: 0;
          border: none;
          border-radius: 0;
          background: transparent;
        }

        .feature-header {
          display: none;
        }

        .feature-title {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 8px;
        }

        .feature-title h5 {
          margin: 0;
          font-size: 18px;
          font-weight: 600;
          color: #1e40af;
        }

        .beta-badge {
          background: #f59e0b;
          color: white;
          padding: 2px 6px;
          border-radius: 4px;
          font-size: 10px;
          font-weight: 600;
          text-transform: uppercase;
        }

        .feature-description {
          color: #374151;
          font-size: 14px;
          margin: 0;
          line-height: 1.5;
        }

        .player-container {
          margin: 0;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .player-overlay-shell {
          position: relative;
          width: 100%;
          height: 100%;
          min-width: 0;
        }

        .coaching-container {
          margin-top: 4px;
        }

        .features-status {
          display: flex;
          gap: 16px;
          margin-top: 16px;
          padding: 12px;
          background: rgba(255, 255, 255, 0.8);
          border-radius: 8px;
          border: 1px solid #e5e7eb;
          flex-wrap: wrap;
        }

        .feature-indicator {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          color: #374151;
          font-weight: 500;
        }

        .status-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #10b981;
        }

        .status-dot.active {
          background: #10b981;
          animation: pulse 2s infinite;
        }

        .loading-synchronized {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 30px;
          color: #6b7280;
        }

        .generating-overlay {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 40px;
          background: #f0f9ff;
          border-radius: 8px;
          margin: 20px 0;
        }

        .generating-overlay h4 {
          margin: 16px 0 8px 0;
          color: #0c4a6e;
          font-size: 18px;
        }

        .generating-overlay p {
          margin: 0;
          color: #075985;
          font-size: 14px;
        }

        .waiting-overlay {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 30px;
          background: #f9fafb;
          border-radius: 8px;
          margin: 20px 0;
        }

        .waiting-overlay p {
          margin: 0;
          color: #6b7280;
          font-size: 14px;
        }

        .video-unavailable {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 40px 30px;
          background: #fef3c7;
          border: 1px solid #f59e0b;
          border-radius: 8px;
          margin: 20px 0;
          text-align: center;
        }

        .video-unavailable h4 {
          margin: 12px 0 8px 0;
          color: #92400e;
          font-size: 18px;
          font-weight: 600;
        }

        .video-unavailable p {
          margin: 0 0 4px 0;
          color: #78350f;
          font-size: 14px;
        }

        .video-unavailable-hint {
          margin-top: 8px !important;
          font-size: 12px !important;
          color: #a16207 !important;
          font-style: italic;
        }

        .loading-spinner {
          width: 24px;
          height: 24px;
          border: 2px solid #e5e7eb;
          border-top: 2px solid #3b82f6;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 12px;
        }

        .traditional-coaching {
          margin-top: 16px;
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        /* Responsive adjustments */
        @media (max-width: 768px) {
          .features-status {
            flex-direction: column;
            gap: 8px;
          }

          .synchronized-features {
            padding: 16px;
          }

          .feature-title {
            flex-wrap: wrap;
          }
        }

        /* Mobile Small (< 480px) */
        @media (max-width: 480px) {
          .video-analysis-upload {
            padding: 0;
            margin: 0;
            border-radius: 0;
            border: none;
          }

          .analysis-results {
            padding: 8px 0;
          }

          .dropzone {
            padding: 16px 12px;
          }

          .upload-icon-wrapper {
            width: 40px;
            height: 40px;
            margin-bottom: 8px;
          }

          .upload-icon {
            width: 20px;
            height: 20px;
          }

          .drop-title {
            font-size: 14px;
          }

          .drop-hint {
            font-size: 12px;
          }

          .element-selector {
            margin-top: 12px;
          }

          .element-select {
            padding: 12px;
            font-size: 16px; /* Prevents iOS zoom on focus */
            min-height: 44px;
          }

          .action-buttons {
            flex-direction: column;
            gap: 8px;
          }

          .analyze-button,
          .reset-button {
            width: 100%;
            justify-content: center;
            min-height: 48px;
            padding: 14px 24px;
          }

          .analysis-header-line {
            flex-direction: row;
            flex-wrap: wrap;
            gap: 4px;
            padding: 10px 8px;
            font-size: 11px;
            margin: 0 0 0 0;
            width: 100%;
            border-radius: 0;
            justify-content: center;
          }

          .header-item {
            font-size: 11px;
          }

          .header-divider {
            display: none;
          }
          
          .coaching-feedback {
            padding: 12px 8px;
            margin: 0;
            width: 100%;
            border-radius: 0;
          }
          
          .coaching-feedback h5 {
            font-size: 14px;
          }
          
          .feedback-content {
            font-size: 13px;
          }
          
          .synchronized-features {
            padding: 12px 8px;
            margin-top: 8px;
            width: 100%;
          }
          
          .feature-title h5 {
            font-size: 16px;
          }
          
          .feature-description {
            font-size: 13px;
          }
          
          .new-analysis-button {
            width: 100%;
            padding: 14px;
            min-height: 48px;
            margin: 0;
          }
          .analysis-action-buttons {
            flex-direction: column;
            gap: 8px;
          }
          
          .generating-overlay {
            padding: 24px 12px;
          }
          
          .generating-overlay h4 {
            font-size: 16px;
          }
        }

        /* Dark Mode Overrides */
        body[data-theme="dark"] .mode-switch {
          border-color: #475569;
        }
        body[data-theme="dark"] .mode-btn {
          background: #1e293b;
          color: #94a3b8;
        }
        body[data-theme="dark"] .mode-btn.active {
          background: #3b82f6;
          color: white;
        }
        body[data-theme="dark"] .mode-btn:hover:not(.active) {
          background: #334155;
        }

        body[data-theme="dark"] .video-analysis-upload {
          background: rgba(30, 41, 59, 0.95);
          border-color: rgba(51, 65, 85, 0.3);
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
        }

        body[data-theme="dark"] .dropzone {
          background: rgba(30, 41, 59, 0.5);
          border-color: rgba(51, 65, 85, 0.5);
        }

        body[data-theme="dark"] .dropzone:hover,
        body[data-theme="dark"] .dropzone.dragactive {
          background: rgba(30, 41, 59, 0.8);
          border-color: #60a5fa;
        }

        body[data-theme="dark"] .dropzone.has-file {
          background: rgba(6, 78, 59, 0.3);
          border-color: rgba(52, 211, 153, 0.5);
        }

        body[data-theme="dark"] .upload-icon-wrapper {
          background: rgba(51, 65, 85, 0.4);
          border-color: rgba(71, 85, 105, 0.4);
        }

        body[data-theme="dark"] .upload-icon {
          color: #94a3b8;
        }

        body[data-theme="dark"] .dropzone:hover .upload-icon-wrapper {
          background: rgba(59, 130, 246, 0.1);
          border-color: rgba(96, 165, 250, 0.3);
        }

        body[data-theme="dark"] .dropzone:hover .upload-icon {
          color: #60a5fa;
        }

        body[data-theme="dark"] .drop-title {
          color: #e2e8f0;
        }

        body[data-theme="dark"] .drop-hint {
          color: #64748b;
        }

        body[data-theme="dark"] .file-name {
          color: #f1f5f9;
        }

        body[data-theme="dark"] .element-select {
          background: #1e293b;
          border-color: rgba(51, 65, 85, 0.5);
          color: #f1f5f9;
        }

        body[data-theme="dark"] .reset-button {
          background: #334155;
          color: #cbd5e1;
        }

        body[data-theme="dark"] .reset-button:hover {
          background: #475569;
          color: #f1f5f9;
        }

        body[data-theme="dark"] .analysis-header-line {
          background: rgba(15, 23, 42, 0.6);
          border-color: rgba(51, 65, 85, 0.5);
          color: #e2e8f0;
        }

        body[data-theme="dark"] .header-divider {
          color: #475569;
        }

        body[data-theme="dark"] .coaching-feedback {
          background: rgba(30, 58, 138, 0.2);
          border: 1px solid rgba(30, 64, 175, 0.3);
        }

        body[data-theme="dark"] .coaching-feedback h5 {
          color: #bfdbfe;
        }

        body[data-theme="dark"] .feedback-content {
          color: #dbeafe;
        }

        body[data-theme="dark"] .synchronized-features {
          background: transparent;
          border-color: transparent;
        }

        body[data-theme="dark"] .feature-title h5 {
          color: #93c5fd;
        }

        body[data-theme="dark"] .feature-description {
          color: #94a3b8;
        }

        body[data-theme="dark"] .features-status {
          background: rgba(15, 23, 42, 0.6);
          border-color: rgba(51, 65, 85, 0.5);
        }

        body[data-theme="dark"] .feature-indicator {
          color: #cbd5e1;
        }

        body[data-theme="dark"] .generating-overlay,
        body[data-theme="dark"] .waiting-overlay {
          background: rgba(15, 23, 42, 0.5);
        }

        body[data-theme="dark"] .generating-overlay h4 {
          color: #bae6fd;
        }

        body[data-theme="dark"] .generating-overlay p,
        body[data-theme="dark"] .waiting-overlay p {
          color: #94a3b8;
        }

        /* Premium Gold Mode Overrides */
        body[data-theme="gold"] .mode-switch {
          border-color: rgba(212, 175, 55, 0.3);
        }
        body[data-theme="gold"] .mode-btn {
          background: rgba(42, 42, 42, 0.8);
          color: rgba(244, 228, 188, 0.6);
        }
        body[data-theme="gold"] .mode-btn.active {
          background: linear-gradient(135deg, #8B6914, #D4AF37);
          color: white;
        }
        body[data-theme="gold"] .mode-btn:hover:not(.active) {
          background: rgba(212, 175, 55, 0.1);
        }

        body[data-theme="gold"] .video-analysis-upload {
          background: rgba(26, 26, 26, 0.95);
          border-color: rgba(212, 175, 55, 0.3);
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(212, 175, 55, 0.1);
        }

        body[data-theme="gold"] .dropzone {
          background: rgba(42, 42, 42, 0.8);
          border-color: rgba(212, 175, 55, 0.3);
          position: relative;
        }

        body[data-theme="gold"] .dropzone::after {
          content: '';
          position: absolute;
          top: 0;
          left: 10%;
          right: 10%;
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(212, 175, 55, 0.3), transparent);
        }

        body[data-theme="gold"] .dropzone:hover {
          background: rgba(42, 42, 42, 0.9);
          border-color: rgba(212, 175, 55, 0.6);
        }

        body[data-theme="gold"] .dropzone:hover .upload-icon-wrapper {
          transform: translateY(-2px);
          box-shadow: 0 0 20px rgba(212, 175, 55, 0.15);
          border-color: rgba(212, 175, 55, 0.5);
        }

        body[data-theme="gold"] .dropzone:hover .upload-icon {
          color: #d4af37;
        }

        body[data-theme="gold"] .dropzone.dragactive {
          background: rgba(42, 42, 42, 0.95);
          border-color: rgba(212, 175, 55, 0.7);
        }

        body[data-theme="gold"] .dropzone.dragactive .upload-icon-wrapper {
          box-shadow: 0 0 28px rgba(212, 175, 55, 0.25);
          border-color: rgba(212, 175, 55, 0.6);
        }

        body[data-theme="gold"] .dropzone.dragactive .upload-icon {
          color: #f4e4bc;
        }

        body[data-theme="gold"] .dropzone.has-file {
          background: rgba(16, 185, 129, 0.1);
          border-color: rgba(16, 185, 129, 0.4);
        }

        body[data-theme="gold"] .upload-icon-wrapper {
          background: rgba(212, 175, 55, 0.08);
          border-color: rgba(212, 175, 55, 0.25);
          transition: all 0.3s ease;
        }

        body[data-theme="gold"] .upload-icon {
          color: rgba(212, 175, 55, 0.7);
        }

        body[data-theme="gold"] .drop-title {
          color: #f4e4bc;
        }

        body[data-theme="gold"] .drop-hint {
          color: rgba(244, 228, 188, 0.5);
        }

        body[data-theme="gold"] .file-name {
          color: #f5f3ef;
        }

        body[data-theme="gold"] .element-select {
          background: rgba(42, 42, 42, 0.9);
          border-color: rgba(212, 175, 55, 0.3);
          color: #f5f3ef;
        }

        body[data-theme="gold"] .reset-button {
          background: rgba(42, 42, 42, 0.9);
          color: #f4e4bc;
          border: 1px solid rgba(212, 175, 55, 0.3);
        }

        body[data-theme="gold"] .reset-button:hover {
          background: rgba(212, 175, 55, 0.15);
          border-color: rgba(212, 175, 55, 0.5);
        }

        body[data-theme="gold"] .analysis-header-line {
          background: rgba(26, 26, 26, 0.8);
          border-color: rgba(212, 175, 55, 0.3);
          color: #f4e4bc;
        }

        body[data-theme="gold"] .header-divider {
          color: rgba(212, 175, 55, 0.4);
        }

        body[data-theme="gold"] .coaching-feedback {
          background: rgba(42, 42, 42, 0.8);
          border: 1px solid rgba(212, 175, 55, 0.3);
        }

        body[data-theme="gold"] .coaching-feedback h5 {
          color: #f4e4bc;
        }

        body[data-theme="gold"] .feedback-content {
          color: rgba(244, 228, 188, 0.8);
        }

        body[data-theme="gold"] .synchronized-features {
          background: transparent;
          border-color: transparent;
        }

        body[data-theme="gold"] .feature-title h5 {
          color: #f4e4bc;
        }

        body[data-theme="gold"] .feature-description {
          color: rgba(244, 228, 188, 0.7);
        }

        body[data-theme="gold"] .features-status {
          background: rgba(42, 42, 42, 0.9);
          border-color: rgba(212, 175, 55, 0.2);
        }

        body[data-theme="gold"] .feature-indicator {
          color: rgba(244, 228, 188, 0.6);
        }

        body[data-theme="gold"] .generating-overlay,
        body[data-theme="gold"] .waiting-overlay {
          background: rgba(26, 26, 26, 0.9);
        }

        body[data-theme="gold"] .generating-overlay h4 {
          color: #f4e4bc;
        }

        body[data-theme="gold"] .generating-overlay p,
        body[data-theme="gold"] .waiting-overlay p {
          color: rgba(244, 228, 188, 0.7);
        }
      `}</style>
    </div>
  );
};

export default VideoAnalysisUpload;
